'use client';

import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { signOut, useSession } from 'next-auth/react';
import { TbLogout } from 'react-icons/tb';
import { LanguageDropdown } from '@/shared/components/LanguageDropdown';
import { commonTranslation, useNamespacedTranslate } from '@/shared/lib/locales';

export function Header() {
  const t = useNamespacedTranslate('header');

  const { data: authSession } = useSession();
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  const name = authSession?.user?.name || authSession?.user?.email || 'U';
  const initial = name.charAt(0).toUpperCase();

  const handleLogout = () => {
    setUserDropdownOpen(false);
    signOut({ callbackUrl: '/' });
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (userDropdownRef.current && !userDropdownRef.current.contains(target)) {
        setUserDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="relative flex h-16 w-full items-center border-b border-gray-200 bg-white px-6">
      <Link href="/" className="absolute left-1/2 -translate-x-1/2">
        <Image
          src="/assets/svgs/Logo_CJRRenewables.svg"
          alt="CJR Renewables"
          width={140}
          height={44}
          priority
        />
      </Link>

      <div className="ml-auto flex items-center gap-4">
        <LanguageDropdown />

        <div className="relative" ref={userDropdownRef}>
          <button
            onClick={() => setUserDropdownOpen((prev) => !prev)}
            className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-lg font-semibold text-white transition hover:bg-blue-700 focus:outline-none"
            aria-label={t('userMenuAriaLabel')}
          >
            {initial}
          </button>

          {userDropdownOpen && (
            <div className="absolute right-0 top-14 z-50 min-w-[140px] rounded-2xl border border-gray-100 bg-white py-1 shadow-lg">
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-gray-700 transition hover:bg-gray-50"
            >
              <TbLogout className="text-red-500 text-base" />
              {commonTranslation('logout')}
            </button>
          </div>
          )}
        </div>
      </div>
    </header>
  );
}
